import { DomainModel, isValidCustomSlug } from 'domains/models';
import { DisplayType } from './displayType';
import { PeriodType } from './periodType';
import { AvailableUserType } from './availableUserType';
import { CalendarViewType } from './calendarViewType';
import { HostAssignType } from './hostAssignType';
import { RescheduleType } from './rescheduleType';
import { ConfirmationType } from './confirmationType';
import { isUrl } from 'infras/validator';
import { EventTypeType } from './eventTypeType';
import { Status } from './status';
import { defaultCoverImageList } from '../event_type/coverImage';
import EventTypeNotification from '../event_type_notification';
import EventTypeSetting, {
  DEFAULT_MAIL_REMIND_TIMING,
} from '../event_type_setting';
import EventTypeUser from '../event_type_user';
import Form from '../form';
import { PlanId } from '../subscription/planId';
import { CalendarType } from './calendarType';
import { eventTypeThemeColors } from 'domains/models/event_type/color';
import Subscription from '../subscription';
import { ReservationMethod } from './reservationMethod';
import { HostOperationType } from './hostOperationType';
import { flexibleStartWeekDay, StartWeekDay } from './startWeekDay';
import { RoundRobinAllocateType } from './roundRobinAllocateType';
import EventTypeWorkflow from '../event_type_workflow';
import EventTypeCalendarViewTypeFeature from '../subscription/features/event_type_calendar_view_type';
import { PlanVersion } from '../subscription/planVersion';
import { DisplayWeekDay, defaultDisplayWeekDay } from './displayWeekDay';
import { ViewerTimeZoneType } from './viewerTimeZoneType';
import { EventTypeVersion, latestEventTypeVersion } from './eventTypeVersion';
import EventTypeResourceSet from '../event_type_resource_set';
import { GracePeriodPoint, GracePeriodUnit } from './gracePeriodType';
import { AttendType } from './attendType';

export default class EventType extends DomainModel {
  teamId: number;
  uid: string;
  projectId: number | null;
  eventTypeType: EventTypeType;
  name: string;
  coverImage: string | null;
  maxCapacity: number | null;
  duration: number;
  isLimitGracePeriod: boolean;
  gracePeriod: number;
  gracePeriodPoint: GracePeriodPoint;
  gracePeriodUnit: GracePeriodUnit;
  description: string;
  displayType: DisplayType;
  periodType: PeriodType;
  periodDuration: number;
  periodStart: string;
  periodEnd: string;
  googleAnalyticsId: string | null;
  bufferBefore: number;
  bufferAfter: number;
  timeZone: string;
  intervalMinutes: number;
  confirmText: string;
  availableUserType: AvailableUserType;
  calendarViewType: CalendarViewType;
  color: string;
  hostOperationType: HostOperationType;
  hostAssignType: HostAssignType;
  rescheduleType: RescheduleType;
  confirmRedirectUrl: string | null;
  confirmationType: ConfirmationType;
  rescheduleConfirmRedirectUrl: string | null;
  rescheduleConfirmationType: ConfirmationType;
  cancelConfirmRedirectUrl: string | null;
  cancelConfirmationType: ConfirmationType;
  formId: number | null;
  status: Status;
  forceDowngradedAt: Date;
  calendarType: CalendarType;
  eventTypeCalendarTemplateId: number | null;
  isRevealGuestUser: boolean;
  reservationMethod: ReservationMethod;
  startWeekDay: StartWeekDay;
  displayWeekDay: DisplayWeekDay;
  viewerTimeZoneType: ViewerTimeZoneType;
  roundRobinAllocateType: RoundRobinAllocateType;
  isDisplayRemainingCapacity: boolean;
  isDisplayFullCapacity: boolean;
  internalNote: string | null;
  slug: string;
  version: EventTypeVersion;
  attendType: AttendType;
  latestEventCreatedAt: Date | null;

  constructor(props: any) {
    super(props);
    this._className = 'EventType';
    this.uid = props.uid || null;
    this.teamId = props.teamId || null;
    this.projectId = props.projectId || null;
    this.eventTypeType = props.eventTypeType || EventTypeType.Standard;
    // this.userId = props.userId || null;
    this.name = props.name || null;
    this.coverImage = props.coverImage || null;
    this.maxCapacity = props.maxCapacity || null;
    this.duration = props.duration || null;
    this.isLimitGracePeriod = Boolean(props.isLimitGracePeriod);
    this.gracePeriod =
      typeof props.gracePeriod === 'number' ? props.gracePeriod : 60;
    this.gracePeriodPoint = props.gracePeriodPoint;
    this.gracePeriodUnit = props.gracePeriodUnit;
    this.description = props.description || null;
    this.displayType = props.displayType || null;
    this.periodType = props.periodType || PeriodType.RollingDays;
    this.periodDuration = props.periodDuration ?? 60 * 24 * 90; // 3 month
    this.periodStart = props.periodStart || null;
    this.periodEnd = props.periodEnd || null;
    this.bufferBefore = props.bufferBefore || 0;
    this.bufferAfter = props.bufferAfter || 0;
    this.intervalMinutes = props.intervalMinutes || 30;
    this.googleAnalyticsId = props.googleAnalyticsId || null;
    this.timeZone = props.timeZone || null;
    this.confirmText = props.confirmText || null;
    this.availableUserType = props.availableUserType || AvailableUserType.All;
    this.calendarViewType =
      props.calendarViewType || CalendarViewType.TwoColumnMonth;
    this.color = props.color;
    this.hostOperationType = props.hostOperationType || HostOperationType.None;
    this.hostAssignType = props.hostAssignType || HostAssignType.None;
    this.rescheduleType =
      props.rescheduleType || RescheduleType.LockRescheduleUser;
    this.confirmRedirectUrl = props.confirmRedirectUrl || null;
    this.confirmationType = props.confirmationType;
    this.rescheduleConfirmRedirectUrl =
      props.rescheduleConfirmRedirectUrl || null;
    this.rescheduleConfirmationType = props.rescheduleConfirmationType;
    this.cancelConfirmRedirectUrl = props.cancelConfirmRedirectUrl || null;
    this.cancelConfirmationType = props.cancelConfirmationType;
    this.formId = props.formId;
    this.status = props.status;
    this.forceDowngradedAt = props.forceDowngradedAt;
    this.calendarType = props.calendarType || CalendarType.Custom;
    this.eventTypeCalendarTemplateId = props.eventTypeCalendarTemplateId;
    this.isRevealGuestUser = props.isRevealGuestUser || false;
    this.reservationMethod = props.reservationMethod || ReservationMethod.Event;
    this.startWeekDay = props.startWeekDay;
    this.displayWeekDay = props.displayWeekDay || defaultDisplayWeekDay;
    this.viewerTimeZoneType =
      props.viewerTimeZoneType || ViewerTimeZoneType.Auto;
    this.roundRobinAllocateType =
      props.roundRobinAllocateType || RoundRobinAllocateType.AllocateEqually;
    this.isDisplayRemainingCapacity = props.isDisplayRemainingCapacity;
    this.isDisplayFullCapacity = props.isDisplayFullCapacity;
    this.internalNote = props.internalNote;
    this.slug = props.slug;
    this.version = props.version || latestEventTypeVersion;
    this.attendType = props.attendType || AttendType.Normal;
    this.latestEventCreatedAt = props.latestEventCreatedAt;
  }

  static permited = [
    'uid',
    'name',
    'coverImage',
    'maxCapacity',
    'duration',
    'description',
    'displayType',
    'periodType',
    'periodDuration',
    'periodStart',
    'periodEnd',
    'isLimitGracePeriod',
    'gracePeriod',
    'gracePeriodPoint',
    'gracePeriodUnit',
    'timeZone',
    'intervalMinutes',
    'googleAnalyticsId',
    'bufferBefore',
    'bufferAfter',
    'periodEnd',
    'confirmText',
    'availableUserType',
    'calendarViewType',
    'hostOperationType',
    'hostAssignType',
    'rescheduleType',
    'confirmationType',
    'confirmRedirectUrl',
    'rescheduleConfirmRedirectUrl',
    'rescheduleConfirmationType',
    'cancelConfirmRedirectUrl',
    'cancelConfirmationType',
    'eventTypeType',
    'projectId',
    'formId',
    'color',
    'status',
    'forceDowngradedAt',
    'calendarType',
    'eventTypeCalendarTemplateId',
    'isRevealGuestUser',
    'startWeekDay',
    'displayWeekDay',
    'viewerTimeZoneType',
    'roundRobinAllocateType',
    'isDisplayRemainingCapacity',
    'isDisplayFullCapacity',
    'internalNote',
    'slug',
    'latestEventCreatedAt',
  ];

  static permit(updater: any) {
    return super.permit(updater, this.permited);
  }

  isValid(
    scopes: string[] | null = null,
    subscription: Subscription | null = null,
    skipScope: { timeZone: boolean } | undefined = undefined,
  ) {
    super.beforeValidate();
    let p = '';

    p = 'name';
    if (!scopes || scopes.includes(p)) {
      if (!this.name) {
        this.addError(p, 'E001');
      } else {
        if (this.name.length > 32) {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'teamId';
    if (!scopes || scopes.includes(p)) {
      if (!this.teamId) {
        this.addError(p, 'E001');
      }
    }

    p = 'description';
    if (!scopes || scopes.includes(p)) {
      if (this.description) {
        const regex = /(<([^>]+)>)/gi;
        const plainTextDescription = this.description.replace(regex, '');
        if (plainTextDescription.length > 10000) {
          this.addError(p, 'E001');
        }
      }
    }

    p = 'coverImage';
    if (!scopes || scopes.includes(p)) {
      if (this.coverImage) {
      }
    }

    p = 'maxCapacity';
    if (!scopes || scopes.includes(p)) {
      if (this.maxCapacity !== null) {
        if (typeof this.maxCapacity !== 'number') {
          this.addError(p, 'E001');
        } else {
          if (this.maxCapacity > 999) {
            this.addError(p, 'E002');
          }
          if (this.availableUserType === AvailableUserType.AnyOne) {
            this.addError(p, 'E003');
          }
          if (this.maxCapacity <= 1) {
            this.addError(p, 'E004');
          }
        }
      }
    }

    p = 'availableUserType';
    if (!scopes || scopes.includes(p)) {
      if (this.availableUserType) {
        if (
          Number(this.availableUserType) === Number(AvailableUserType.AnyOne) &&
          this.maxCapacity !== null
        ) {
          this.addError(p, 'E001');
        }
      }
    }

    p = 'duration';
    if (!scopes || scopes.includes(p)) {
      if (!this.duration || this.duration <= 0) {
        this.addError(p, 'E001');
      } else {
        if (this.duration > 60 * 24) {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'gracePeriod';
    if (!scopes || scopes.includes(p)) {
      if (this.gracePeriod === null) {
        this.addError(p, 'E001');
      } else {
        if (
          this.gracePeriodUnit === GracePeriodUnit.Minute &&
          this.gracePeriod > 60 * 24
        ) {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'displayType';
    if (!scopes || scopes.includes(p)) {
      if (!this.displayType) {
        this.addError(p, 'E001');
      } else {
        if (typeof DisplayType[this.displayType] !== 'string') {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'periodType';
    if (!scopes || scopes.includes(p)) {
      if (this.periodType) {
      }
    }

    p = 'periodDuration';
    if (!scopes || scopes.includes(p)) {
      if (this.periodDuration) {
      }
    }

    p = 'periodStart';
    if (!scopes || scopes.includes(p)) {
      if (this.periodStart) {
      }
    }

    p = 'periodEnd';
    if (!scopes || scopes.includes(p)) {
      if (this.periodStart) {
      }
    }

    p = 'timeZone';
    if (
      (!scopes || scopes.includes(p)) &&
      (!skipScope || skipScope.timeZone === false)
    ) {
      if (!this.timeZone) {
        this.addError(p, 'E001');
      }
    }

    p = 'confirmText';
    if (!scopes || scopes.includes(p)) {
      if (this.confirmText) {
        if (this.confirmText.length > 400) {
          this.addError(p, 'E001');
        }
      }
    }

    p = 'confirmationType';
    if (!scopes || scopes.includes(p)) {
      if (
        this.confirmationType !== ConfirmationType.Normal &&
        this.confirmationType !== ConfirmationType.Redirect &&
        this.confirmationType !== ConfirmationType.RedirectWithProps
      ) {
        // this.addError(p, 'E001');
      }
    }

    p = 'confirmRedirectUrl';
    if (!scopes || scopes.includes(p)) {
      if (this.confirmRedirectUrl) {
        if (!isUrl(this.confirmRedirectUrl)) {
          this.addError(p, 'E001');
        }
        if (this.confirmRedirectUrl.length > 255) {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'cancelConfirmationType';
    if (!scopes || scopes.includes(p)) {
      if (
        this.cancelConfirmationType !== ConfirmationType.Normal &&
        this.cancelConfirmationType !== ConfirmationType.Redirect &&
        this.cancelConfirmationType !== ConfirmationType.RedirectWithProps
      ) {
        // this.addError(p, 'E001');
      }
    }

    p = 'cancelConfirmRedirectUrl';
    if (!scopes || scopes.includes(p)) {
      if (this.cancelConfirmRedirectUrl) {
        if (!isUrl(this.cancelConfirmRedirectUrl)) {
          this.addError(p, 'E001');
        }
        if (this.cancelConfirmRedirectUrl.length > 255) {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'rescheduleConfirmationType';
    if (!scopes || scopes.includes(p)) {
      if (
        this.rescheduleConfirmationType !== ConfirmationType.Normal &&
        this.rescheduleConfirmationType !== ConfirmationType.Redirect &&
        this.rescheduleConfirmationType !== ConfirmationType.RedirectWithProps
      ) {
        // this.addError(p, 'E001');
      }
    }

    p = 'rescheduleConfirmRedirectUrl';
    if (!scopes || scopes.includes(p)) {
      if (this.rescheduleConfirmRedirectUrl) {
        if (!isUrl(this.rescheduleConfirmRedirectUrl)) {
          this.addError(p, 'E001');
        }
        if (this.rescheduleConfirmRedirectUrl.length > 255) {
          this.addError(p, 'E002');
        }
      }
    }

    p = 'isRevealGuestUser';
    if (!scopes || scopes.includes(p)) {
      if (
        this.isRevealGuestUser &&
        this.eventTypeType === EventTypeType.Standard
      ) {
        if (subscription && !subscription.isAvailableRevealGuestUser) {
          this.addError(p, 'E001');
        }
      }
    }

    p = 'startWeekDay';
    if (!scopes || scopes.includes(p)) {
      if (
        this.startWeekDay < 0 ||
        this.startWeekDay > 7 ||
        this.startWeekDay === flexibleStartWeekDay
      ) {
        this.addError(p, 'E001');
      }
    }

    p = 'displayWeekDay';
    if (!scopes || scopes.includes(p)) {
      if (!this.displayWeekDay) {
        this.addError(p, 'E001');
      } else {
        const checkDuplicate: { [key: string]: boolean } = {};
        this.displayWeekDay.split(',').forEach((e) => {
          if (!Number.isInteger(Number(e))) {
            this.addError(p, 'E002');
          }
          if (Number(e) < 0 || 7 < Number(e)) {
            this.addError(p, 'E002');
          }
          if (checkDuplicate[e]) {
            this.addError(p, 'E003');
          }
          checkDuplicate[e] = true;
        });
      }
    }

    p = 'viewerTimeZoneType';
    if (!scopes || scopes.includes(p)) {
      if (
        ![ViewerTimeZoneType.Auto, ViewerTimeZoneType.Fixed].includes(
          this.viewerTimeZoneType,
        )
      ) {
        this.addError(p, 'E001');
      }
    }

    p = 'internalNote';
    if (!scopes || scopes.includes(p)) {
      if (this.internalNote) {
        if (this.internalNote.length > 128) {
          this.addError(p, 'E001');
        }
      }
    }

    p = 'slug';
    if (!scopes || scopes.includes(p)) {
      if (!this.slug) {
        this.addError(p, 'E004');
      } else {
        if (this.slug.length > 32) {
          this.addError(p, 'E002');
        }
        if (!isValidCustomSlug(this.slug)) {
          this.addError(p, 'E003');
        }
        if (this.slug.length < 4) {
          this.addError(p, 'E005');
        }
      }
    }
    p = 'googleAnalyticsId';
    if (!scopes || scopes.includes(p)) {
      if (this.googleAnalyticsId) {
        if (this.googleAnalyticsId.length > 32) {
          this.addError(p, 'E001');
        }
      }
    }
    return super.isValid();
  }

  isValidBookingRequest(eventTypeSetting: EventTypeSetting) {
    let p = '';

    p = 'availableUserType';
    if (eventTypeSetting.isBookingRequest && EventType.isRoundRobin(this)) {
      this.addError(p, 'E002');
    }

    p = 'maxCapacity';
    if (eventTypeSetting.isBookingRequest && EventType.isSeminar(this)) {
      this.addError(p, 'E005');
    }

    return super.isValid();
  }

  static isAvailableProFeatures(
    eventType: EventType,
    eventTypeUsers: EventTypeUser[],
    eventTypeNotification: EventTypeNotification | null,
    eventTypeSetting: EventTypeSetting | null,
    form: Form | null,
    eventTypeWorkflows: EventTypeWorkflow[],
    eventTypeResourceSets: EventTypeResourceSet[],
  ): boolean {
    if (
      eventType.googleAnalyticsId ||
      eventType.maxCapacity !== null ||
      eventType.hostAssignType === HostAssignType.RoundRobin ||
      eventType.confirmationType === ConfirmationType.Redirect ||
      eventType.confirmationType === ConfirmationType.RedirectWithProps ||
      eventType.rescheduleConfirmationType === ConfirmationType.Redirect ||
      eventType.rescheduleConfirmationType ===
        ConfirmationType.RedirectWithProps ||
      eventType.cancelConfirmationType === ConfirmationType.Redirect ||
      eventType.cancelConfirmationType === ConfirmationType.RedirectWithProps
    ) {
      console.log('eventType', eventType.id);
      return true;
    }

    if (eventTypeNotification) {
      if (
        eventTypeNotification.calendarInviteBody ||
        eventTypeNotification.mailConfirmBody ||
        (eventTypeNotification.mailRemindBody &&
          eventTypeNotification.mailRemindBody.replace(/\r?\n/g, '')) ||
        eventTypeNotification.mailCancelBody ||
        eventTypeNotification.mailFollowUpBody
      ) {
        console.log('eventTypeNotification', eventType.id);
        return true;
      }
    }

    if (eventTypeSetting) {
      if (eventTypeSetting.isMailFollowUp) {
        console.log('eventTypeSetting', eventType.id);
        return true;
      }
    }

    if (eventTypeWorkflows) {
      const mailWorkflows = eventTypeWorkflows.filter((w) =>
        EventTypeWorkflow.isRemindMail(w),
      );
      if (
        mailWorkflows.length > 1 ||
        mailWorkflows.some((w) => w.timing !== DEFAULT_MAIL_REMIND_TIMING)
      ) {
        console.log('eventTypeWorkflows', eventType.id);
        return true;
      }
    }

    if (
      Array.isArray(eventTypeResourceSets) &&
      eventTypeResourceSets.length > 0
    ) {
      return true;
    }

    return false;
  }

  static isAvailableStartupFeatures(
    planId: PlanId,
    planVersion: PlanVersion,
    eventType: EventType,
    eventTypeUsers: EventTypeUser[],
    eventTypeNotification: EventTypeNotification | null,
    eventTypeSetting: EventTypeSetting | null,
    form: Form | null,
  ): boolean {
    if (
      eventType.availableUserType === AvailableUserType.AnyOne ||
      (eventType.coverImage &&
        defaultCoverImageList.every(
          (img) => img.categoryCoverImg !== eventType.coverImage,
        ))
    ) {
      console.log('eventType startup: ', eventType.id);
      return true;
    }

    if (eventType.eventTypeType === EventTypeType.Standard && eventTypeUsers) {
      if (eventTypeUsers.length > 1) {
        console.log('eventTypeUsers', eventType.id);
        return true;
      }
    }

    const eventTypeCalendarViewTypeFeature = new EventTypeCalendarViewTypeFeature(
      { planVersion: planVersion },
    );
    if (
      !eventTypeCalendarViewTypeFeature.isValid(
        planId,
        eventType.calendarViewType,
      )
    ) {
      return true;
    }

    if (form) {
      if (typeof form.workflows === 'string') {
        const workflows = JSON.parse(form.workflows);
        if (workflows.length > 0) {
          console.log('form', eventType.id);
          return true;
        }
      }
    }

    return false;
  }

  static paidFeaturesDisableParams(eventType: EventType, planId: PlanId) {
    let calendarViewType = eventType.calendarViewType;
    if (planId === PlanId.Free) {
      if (
        [CalendarViewType.Week, CalendarViewType.TwoColumnMonthAccent].includes(
          eventType.calendarViewType,
        )
      ) {
        calendarViewType = CalendarViewType.TwoColumnMonth;
      }
      return {
        formId: null,
        googleAnalyticsId: null,
        maxCapacity: null,
        availableUserType: AvailableUserType.All,
        hostAssignType: HostAssignType.None,
        confirmationType: ConfirmationType.Normal,
        calendarViewType,
        coverImage: defaultCoverImageList[0].categoryCoverImg,
        isDisplayFullCapacity: false,
      };
    } else if (planId === PlanId.Pro) {
      return {
        googleAnalyticsId: null,
        maxCapacity: null,
        hostAssignType: HostAssignType.None,
        confirmationType: ConfirmationType.Normal,
        calendarViewType,
        isDisplayFullCapacity: false,
      };
    } else {
      return {};
    }
  }

  static generateEventTypeColorById(id: number): string {
    const num = id % eventTypeThemeColors.length;
    return eventTypeThemeColors[num] || eventTypeThemeColors[0];
  }

  static isRoundRobin(eventType: EventType) {
    return (
      eventType.availableUserType === AvailableUserType.AnyOne &&
      eventType.hostAssignType === HostAssignType.RoundRobin
    );
  }

  static isSeminar(eventType: EventType) {
    return !!eventType.maxCapacity && 0 < eventType.maxCapacity;
  }
}
